import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import productMasterService from '../../services/productMasterService'
import supplierMasterService from '../../services/supplierMasterService'

const ProductMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const productData = location.state?.productData

  const [formData, setFormData] = useState({
    productCode: '',
    productName: '',
    productDescription: '',
    category: '',
    productType: 'Goods',
    unitOfMeasurement: 'Unit',
    pricing: {
      basePrice: 0,
      currency: 'INR',
      costPrice: 0,
      sellingPrice: 0,
      discount: 0,
      taxRate: 0,
      taxType: 'GST'
    },
    inventory: {
      trackInventory: false,
      currentStock: 0,
      minimumStock: 0,
      maximumStock: 0,
      reorderPoint: 0,
      reorderQuantity: 0,
      warehouseLocation: ''
    },
    specifications: {
      brand: '',
      model: '',
      manufacturer: '',
      partNumber: '',
      serialNumber: '',
      weight: 0,
      weightUnit: 'kg',
      dimensions: {
        length: 0,
        width: 0,
        height: 0,
        unit: 'cm'
      },
      color: '',
      material: ''
    },
    preferredSuppliers: [],
    images: [],
    status: 'Active',
    barcode: '',
    sku: '',
    hsnCode: '',
    sacCode: '',
    notes: ''
  })

  const [suppliers, setSuppliers] = useState([])
  const [isLoadingProductCode, setIsLoadingProductCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newSupplier, setNewSupplier] = useState({
    supplier: '',
    supplierName: '',
    supplierCode: '',
    supplierPrice: 0,
    leadTime: 0,
    isPrimary: false
  })

  // Fetch next product code (only for new products)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchProductCode = async () => {
        try {
          setIsLoadingProductCode(true)
          const response = await productMasterService.getNextProductCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              productCode: response.data.productCode || response.data.formattedValue || 'PRD0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching product code:', error)
          setFormData(prev => ({ ...prev, productCode: 'PRD0001' }))
        } finally {
          setIsLoadingProductCode(false)
        }
      }
      fetchProductCode()
    }
  }, [isEditMode, isViewMode])

  // Load suppliers
  useEffect(() => {
    loadSuppliers()
  }, [])

  // Load product data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && productData?._id) {
      loadProductData(productData._id)
    }
  }, [isEditMode, isViewMode, productData])

  const loadSuppliers = async () => {
    try {
      const response = await supplierMasterService.getAllSuppliers({ status: 'Active', limit: 1000 })
      if (response.success) {
        setSuppliers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading suppliers:', error)
    }
  }

  const loadProductData = async (id) => {
    try {
      setIsLoading(true)
      const response = await productMasterService.getProductById(id)
      if (response.success && response.data) {
        const product = response.data
        setFormData({
          productCode: product.productCode || '',
          productName: product.productName || '',
          productDescription: product.productDescription || '',
          category: product.category || '',
          productType: product.productType || 'Goods',
          unitOfMeasurement: product.unitOfMeasurement || 'Unit',
          pricing: product.pricing || {
            basePrice: 0,
            currency: 'INR',
            costPrice: 0,
            sellingPrice: 0,
            discount: 0,
            taxRate: 0,
            taxType: 'GST'
          },
          inventory: product.inventory || {
            trackInventory: false,
            currentStock: 0,
            minimumStock: 0,
            maximumStock: 0,
            reorderPoint: 0,
            reorderQuantity: 0,
            warehouseLocation: ''
          },
          specifications: product.specifications || {
            brand: '',
            model: '',
            manufacturer: '',
            partNumber: '',
            serialNumber: '',
            weight: 0,
            weightUnit: 'kg',
            dimensions: {
              length: 0,
              width: 0,
              height: 0,
              unit: 'cm'
            },
            color: '',
            material: ''
          },
          preferredSuppliers: product.preferredSuppliers || [],
          images: product.images || [],
          status: product.status || 'Active',
          barcode: product.barcode || '',
          sku: product.sku || '',
          hsnCode: product.hsnCode || '',
          sacCode: product.sacCode || '',
          notes: product.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading product data:', error)
      toast.error('Failed to load product data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('pricing.')) {
      const pricingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          [pricingField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('inventory.')) {
      const inventoryField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          [inventoryField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
      }))
    } else if (name.startsWith('specifications.dimensions.')) {
      const dimField = name.split('.')[2]
      setFormData(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          dimensions: {
            ...prev.specifications.dimensions,
            [dimField]: type === 'number' ? parseFloat(value) || 0 : value
          }
        }
      }))
    } else if (name.startsWith('specifications.')) {
      const specField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [specField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addSupplier = () => {
    if (!newSupplier.supplier) {
      toast.error('Please select a supplier')
      return
    }
    
    const selectedSupplier = suppliers.find(s => s._id === newSupplier.supplier)
    setFormData(prev => ({
      ...prev,
      preferredSuppliers: [...prev.preferredSuppliers, {
        ...newSupplier,
        supplierName: selectedSupplier?.supplierName || '',
        supplierCode: selectedSupplier?.supplierCode || ''
      }]
    }))
    
    setNewSupplier({
      supplier: '',
      supplierName: '',
      supplierCode: '',
      supplierPrice: 0,
      leadTime: 0,
      isPrimary: false
    })
  }

  const removeSupplier = (index) => {
    setFormData(prev => ({
      ...prev,
      preferredSuppliers: prev.preferredSuppliers.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.productName) {
        toast.error('Please fill in all required fields (Product Name)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && productData?._id) {
        const response = await productMasterService.updateProduct(productData._id, formData)
        if (response.success) {
          toast.success('Product updated successfully!')
          navigate('/procurement/product-master')
        } else {
          toast.error(response.message || 'Failed to update product')
        }
      } else {
        const response = await productMasterService.createProduct(formData)
        if (response.success) {
          toast.success('Product created successfully!')
          navigate('/procurement/product-master')
        } else {
          toast.error(response.message || 'Failed to create product')
        }
      }
    } catch (error) {
      console.error('Error saving product:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save product')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadProductData(productData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        productName: '',
        productDescription: '',
        category: '',
        productType: 'Goods',
        unitOfMeasurement: 'Unit',
        pricing: {
          basePrice: 0,
          currency: 'INR',
          costPrice: 0,
          sellingPrice: 0,
          discount: 0,
          taxRate: 0,
          taxType: 'GST'
        },
        inventory: {
          trackInventory: false,
          currentStock: 0,
          minimumStock: 0,
          maximumStock: 0,
          reorderPoint: 0,
          reorderQuantity: 0,
          warehouseLocation: ''
        },
        specifications: {
          brand: '',
          model: '',
          manufacturer: '',
          partNumber: '',
          serialNumber: '',
          weight: 0,
          weightUnit: 'kg',
          dimensions: {
            length: 0,
            width: 0,
            height: 0,
            unit: 'cm'
          },
          color: '',
          material: ''
        },
        preferredSuppliers: [],
        images: [],
        status: 'Active',
        barcode: '',
        sku: '',
        hsnCode: '',
        sacCode: '',
        notes: ''
      }))
      setNewSupplier({
        supplier: '',
        supplierName: '',
        supplierCode: '',
        supplierPrice: 0,
        leadTime: 0,
        isPrimary: false
      })
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Products', path: '/procurement/product-master' },
    { 
      label: isEditMode ? 'Edit Product' : isViewMode ? 'View Product' : 'New Product', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading product...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Product' : isViewMode ? 'View Product' : 'New Product'}</h1>
          <p>{isEditMode ? 'Update product information' : isViewMode ? 'View product information' : 'Create a new product'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/product-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Product Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Product Code, Product Name, Product Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="productCode" className="form-label required">
                Product Code
              </label>
              <input
                type="text"
                id="productCode"
                name="productCode"
                value={isLoadingProductCode ? 'Generating...' : formData.productCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="productName" className="form-label required">
                Product Name
              </label>
              <input
                type="text"
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Laptop Computer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="productType" className="form-label">
                Product Type
              </label>
              <select
                id="productType"
                name="productType"
                value={formData.productType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Goods">Goods</option>
                <option value="Service">Service</option>
                <option value="Software">Software</option>
                <option value="Hardware">Hardware</option>
                <option value="Consumable">Consumable</option>
                <option value="Asset">Asset</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Discontinued">Discontinued</option>
                <option value="Out of Stock">Out of Stock</option>
              </select>
            </div>
          </div>

          {/* Row 2: Category, Unit of Measurement, SKU, Barcode */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Product category"
              />
            </div>

            <div className="form-group">
              <label htmlFor="unitOfMeasurement" className="form-label">
                Unit of Measurement
              </label>
              <input
                type="text"
                id="unitOfMeasurement"
                name="unitOfMeasurement"
                value={formData.unitOfMeasurement}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., Unit, kg, L"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sku" className="form-label">
                SKU
              </label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Stock keeping unit"
              />
            </div>

            <div className="form-group">
              <label htmlFor="barcode" className="form-label">
                Barcode
              </label>
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Barcode number"
              />
            </div>
          </div>

          {/* Row 3: HSN Code, SAC Code */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="hsnCode" className="form-label">
                HSN Code
              </label>
              <input
                type="text"
                id="hsnCode"
                name="hsnCode"
                value={formData.hsnCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Harmonized System of Nomenclature"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sacCode" className="form-label">
                SAC Code
              </label>
              <input
                type="text"
                id="sacCode"
                name="sacCode"
                value={formData.sacCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Service Accounting Code"
              />
            </div>
          </div>

          {/* Product Description */}
          <div className="form-group">
            <label htmlFor="productDescription" className="form-label">
              Description
            </label>
            <textarea
              id="productDescription"
              name="productDescription"
              value={formData.productDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Product description..."
            />
          </div>

          {/* Pricing Information */}
          <div className="section-header"><h3>Pricing Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="pricing.basePrice" className="form-label">
                Base Price
              </label>
              <input
                type="number"
                id="pricing.basePrice"
                name="pricing.basePrice"
                value={formData.pricing.basePrice}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.costPrice" className="form-label">
                Cost Price
              </label>
              <input
                type="number"
                id="pricing.costPrice"
                name="pricing.costPrice"
                value={formData.pricing.costPrice}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.sellingPrice" className="form-label">
                Selling Price
              </label>
              <input
                type="number"
                id="pricing.sellingPrice"
                name="pricing.sellingPrice"
                value={formData.pricing.sellingPrice}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.currency" className="form-label">
                Currency
              </label>
              <select
                id="pricing.currency"
                name="pricing.currency"
                value={formData.pricing.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="pricing.discount" className="form-label">
                Discount (%)
              </label>
              <input
                type="number"
                id="pricing.discount"
                name="pricing.discount"
                value={formData.pricing.discount}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.taxRate" className="form-label">
                Tax Rate (%)
              </label>
              <input
                type="number"
                id="pricing.taxRate"
                name="pricing.taxRate"
                value={formData.pricing.taxRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.taxType" className="form-label">
                Tax Type
              </label>
              <select
                id="pricing.taxType"
                name="pricing.taxType"
                value={formData.pricing.taxType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="GST">GST</option>
                <option value="VAT">VAT</option>
                <option value="Sales Tax">Sales Tax</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>

          {/* Inventory Information */}
          <div className="section-header"><h3>Inventory Information</h3></div>

          <div className="form-group">
            <label htmlFor="inventory.trackInventory" className="form-label">
              Track Inventory
            </label>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
              <input
                type="checkbox"
                id="inventory.trackInventory"
                name="inventory.trackInventory"
                checked={formData.inventory.trackInventory}
                onChange={handleInputChange}
                disabled={isViewMode}
                style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="inventory.trackInventory" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                Enable inventory tracking
              </label>
            </div>
          </div>

          {formData.inventory.trackInventory && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="inventory.currentStock" className="form-label">
                  Current Stock
                </label>
                <input
                  type="number"
                  id="inventory.currentStock"
                  name="inventory.currentStock"
                  value={formData.inventory.currentStock}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="inventory.minimumStock" className="form-label">
                  Minimum Stock
                </label>
                <input
                  type="number"
                  id="inventory.minimumStock"
                  name="inventory.minimumStock"
                  value={formData.inventory.minimumStock}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="inventory.maximumStock" className="form-label">
                  Maximum Stock
                </label>
                <input
                  type="number"
                  id="inventory.maximumStock"
                  name="inventory.maximumStock"
                  value={formData.inventory.maximumStock}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="inventory.reorderPoint" className="form-label">
                  Reorder Point
                </label>
                <input
                  type="number"
                  id="inventory.reorderPoint"
                  name="inventory.reorderPoint"
                  value={formData.inventory.reorderPoint}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>
            </div>
          )}

          {formData.inventory.trackInventory && (
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="inventory.reorderQuantity" className="form-label">
                  Reorder Quantity
                </label>
                <input
                  type="number"
                  id="inventory.reorderQuantity"
                  name="inventory.reorderQuantity"
                  value={formData.inventory.reorderQuantity}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="inventory.warehouseLocation" className="form-label">
                  Warehouse Location
                </label>
                <input
                  type="text"
                  id="inventory.warehouseLocation"
                  name="inventory.warehouseLocation"
                  value={formData.inventory.warehouseLocation}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  placeholder="Warehouse location"
                />
              </div>
            </div>
          )}

          {/* Product Specifications */}
          <div className="section-header"><h3>Product Specifications</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="specifications.brand" className="form-label">
                Brand
              </label>
              <input
                type="text"
                id="specifications.brand"
                name="specifications.brand"
                value={formData.specifications.brand}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Brand name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.model" className="form-label">
                Model
              </label>
              <input
                type="text"
                id="specifications.model"
                name="specifications.model"
                value={formData.specifications.model}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Model number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.manufacturer" className="form-label">
                Manufacturer
              </label>
              <input
                type="text"
                id="specifications.manufacturer"
                name="specifications.manufacturer"
                value={formData.specifications.manufacturer}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Manufacturer name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.partNumber" className="form-label">
                Part Number
              </label>
              <input
                type="text"
                id="specifications.partNumber"
                name="specifications.partNumber"
                value={formData.specifications.partNumber}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Part number"
              />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="specifications.weight" className="form-label">
                Weight
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  id="specifications.weight"
                  name="specifications.weight"
                  value={formData.specifications.weight}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                  step="0.01"
                  style={{ flex: 1 }}
                />
                <select
                  name="specifications.weightUnit"
                  value={formData.specifications.weightUnit}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  style={{ width: '80px' }}
                >
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="lb">lb</option>
                  <option value="oz">oz</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="specifications.color" className="form-label">
                Color
              </label>
              <input
                type="text"
                id="specifications.color"
                name="specifications.color"
                value={formData.specifications.color}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Color"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.material" className="form-label">
                Material
              </label>
              <input
                type="text"
                id="specifications.material"
                name="specifications.material"
                value={formData.specifications.material}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Material"
              />
            </div>
          </div>

          {/* Dimensions */}
          <div className="section-header"><h3>Dimensions</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="specifications.dimensions.length" className="form-label">
                Length
              </label>
              <input
                type="number"
                id="specifications.dimensions.length"
                name="specifications.dimensions.length"
                value={formData.specifications.dimensions.length}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.dimensions.width" className="form-label">
                Width
              </label>
              <input
                type="number"
                id="specifications.dimensions.width"
                name="specifications.dimensions.width"
                value={formData.specifications.dimensions.width}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.dimensions.height" className="form-label">
                Height
              </label>
              <input
                type="number"
                id="specifications.dimensions.height"
                name="specifications.dimensions.height"
                value={formData.specifications.dimensions.height}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="specifications.dimensions.unit" className="form-label">
                Unit
              </label>
              <select
                id="specifications.dimensions.unit"
                name="specifications.dimensions.unit"
                value={formData.specifications.dimensions.unit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="cm">cm</option>
                <option value="m">m</option>
                <option value="in">in</option>
                <option value="ft">ft</option>
              </select>
            </div>
          </div>

          {/* Preferred Suppliers */}
          <div className="section-header"><h3>Preferred Suppliers</h3></div>

          {formData.preferredSuppliers.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.preferredSuppliers.map((supplier, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong>{supplier.supplierName || supplier.supplierCode}</strong>
                      {supplier.isPrimary && (
                        <span className="status-badge active" style={{ marginLeft: '8px', fontSize: '12px' }}>
                          Primary
                        </span>
                      )}
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeSupplier(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
                    Price: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(supplier.supplierPrice || 0)} | 
                    Lead Time: {supplier.leadTime || 0} days
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <select
                    value={newSupplier.supplier}
                    onChange={(e) => {
                      const selectedSupplier = suppliers.find(s => s._id === e.target.value)
                      setNewSupplier({
                        ...newSupplier,
                        supplier: e.target.value,
                        supplierName: selectedSupplier?.supplierName || '',
                        supplierCode: selectedSupplier?.supplierCode || ''
                      })
                    }}
                    className="form-input"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier._id} value={supplier._id}>
                        {supplier.supplierName} ({supplier.supplierCode})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier Price</label>
                  <input
                    type="number"
                    value={newSupplier.supplierPrice}
                    onChange={(e) => setNewSupplier({ ...newSupplier, supplierPrice: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Lead Time (Days)</label>
                  <input
                    type="number"
                    value={newSupplier.leadTime}
                    onChange={(e) => setNewSupplier({ ...newSupplier, leadTime: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Primary</label>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={newSupplier.isPrimary}
                      onChange={(e) => setNewSupplier({ ...newSupplier, isPrimary: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                      Primary supplier
                    </label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={addSupplier}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Supplier
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ProductMasterForm

